import { useMutation, useQuery } from "@apollo/client";
import { CREATE_FILE, UPDATE_FILE, DELETE_FILE, UPLOAD_FILE_TO_OPENAI, INGEST_PROPERTY_FILE } from "./mutation";
import { GET_FILE, GET_FILES, GET_FILE_SIGNED_URL, GET_FILE_UPLOAD_URL } from "./query";

const normalizeFileRefInput = (input = {}) => {
  const next = { ...input };
  if (!next.ref && next.reference) {
    next.ref = { ...next.reference };
  }
  if (next.ref) {
    next.ref = {
      id: next.ref.id || "",
      collection: next.ref.collection || "",
      ...(next.ref.schemaType ? { schemaType: next.ref.schemaType } : {}),
    };
  }
  return next;
};

const normalizeFilesQueryInput = (input = {}) => {
  const next = { ...input };
  if (!next.refCollection && next.referenceCollection) {
    next.refCollection = next.referenceCollection;
  }
  if (!next.refId && next.referenceRefId) {
    next.refId = next.referenceRefId;
  }
  delete next.refSchemaType;
  delete next.referenceSchemaType;
  return next;
};

export const apiCreateFile = async (input) => {
  const client = (await import("../index")).default;
  const result = await client.mutate({
    mutation: CREATE_FILE,
    variables: { input: normalizeFileRefInput(input) },
  });
  return result.data.createFile.file;
};

export const apiUpdateFile = async (input) => {
  const client = (await import("../index")).default;
  const result = await client.mutate({
    mutation: UPDATE_FILE,
    variables: { input: normalizeFileRefInput(input) },
  });
  return result.data.updateFile.file;
};

export const apiDeleteFile = async (id, authorId) => {
  const client = (await import("../index")).default;
  const result = await client.mutate({
    mutation: DELETE_FILE,
    variables: { id, authorId },
  });
  return result.data.deleteFile;
};

export const apiGetFile = async (id, authorId) => {
  const client = (await import("../index")).default;
  const result = await client.query({
    query: GET_FILE,
    variables: { id, authorId },
    fetchPolicy: "network-only",
  });
  return result.data.file;
};

export const apiGetFiles = async (input) => {
  const client = (await import("../index")).default;
  const result = await client.query({
    query: GET_FILES,
    variables: { input: normalizeFilesQueryInput(input) },
    fetchPolicy: "network-only",
  });
  return result.data.files;
};

export const apiGetFileUploadUrl = async (key, contentType, isPublic = false) => {
  const client = (await import("../index")).default;
  const input = { key, isPublic };
  if (contentType) {
    input.contentType = contentType;
  }
  const result = await client.query({
    query: GET_FILE_UPLOAD_URL,
    variables: { input },
    fetchPolicy: "network-only",
  });
  return result.data.fileUploadUrl;
};

export const apiGetFileSignedUrl = async (id, authorId) => {
  const client = (await import("../index")).default;
  const result = await client.query({
    query: GET_FILE_SIGNED_URL,
    variables: { input: { id, authorId } },
    fetchPolicy: "network-only",
  });
  return result.data.fileSignedUrl.signedUrl;
};

/**
 * Upload a file to S3 using a pre-signed URL.
 * Returns the S3 key.
 */
export const apiUploadFileToS3 = async (key, file, { contentType, onProgress, isPublic = false } = {}) => {
  const effectiveContentType = contentType || file?.type || "";

  const putWithXhr = (uploadUrl, typeToSend) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      if (typeToSend) {
        xhr.setRequestHeader("Content-Type", typeToSend);
      }
      if (onProgress) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total });
        });
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        let awsCode = "";
        let awsMessage = "";
        if (xhr.responseText) {
          const codeMatch = xhr.responseText.match(/<Code>([^<]+)<\/Code>/);
          const msgMatch = xhr.responseText.match(/<Message>([^<]+)<\/Message>/);
          if (codeMatch) awsCode = codeMatch[1];
          if (msgMatch) awsMessage = msgMatch[1];
        }
        const summary = awsCode
          ? `S3 ${xhr.status} ${awsCode}${awsMessage ? `: ${awsMessage}` : ""}`
          : [xhr.status, xhr.statusText, xhr.responseText].filter(Boolean).join(" ").trim();
        reject(new Error(`Upload failed: ${summary || "unknown error"}`));
      };
      xhr.onerror = () => reject(new Error(`Upload failed: network/CORS error (status ${xhr.status || "0"})`));
      xhr.send(file);
    });

  // Attempt 1: request a signed URL with explicit content type (when available)
  try {
    const { uploadUrl, key: signedKey } = await apiGetFileUploadUrl(
      key,
      effectiveContentType || undefined,
      isPublic
    );
    await putWithXhr(uploadUrl, effectiveContentType || undefined);
    return signedKey;
  } catch (firstError) {
    // Attempt 2: fallback without content type constraint/header for signature compatibility
    const { uploadUrl, key: signedKey } = await apiGetFileUploadUrl(key, undefined, isPublic);
    await putWithXhr(uploadUrl, undefined);
    return signedKey;
  }
};

export const apiIngestPropertyFile = async ({ fileId, sourceName }) => {
  const client = (await import("../index")).default;
  const result = await client.mutate({
    mutation: INGEST_PROPERTY_FILE,
    variables: { input: { fileId, sourceName } },
  });
  return result.data.ingestPropertyFile;
};

export const apiUploadFileToOpenAI = async (id, authorId) => {
  const client = (await import("../index")).default;
  const result = await client.mutate({
    mutation: UPLOAD_FILE_TO_OPENAI,
    variables: { id, authorId },
  });
  return result.data.uploadFileToOpenAI.file;
};

export {
  useQuery,
  useMutation,
  CREATE_FILE,
  UPDATE_FILE,
  DELETE_FILE,
  GET_FILE,
  GET_FILES,
  UPLOAD_FILE_TO_OPENAI,
  INGEST_PROPERTY_FILE,
};
